import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search,
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Receipt,
  Building2,
  Banknote,
  Briefcase,
  CalendarDays,
  Utensils,
  Car,
  Fuel,
  Home,
  ShoppingCart,
  Phone,
  FileText,
  MoreHorizontal,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

// Import API methods
import { officeExpenseAPI, handleAPIError } from './api';
import RecordSalariesDialog from './RecordSalariesDialog';

// ---------------------------------------------------------------------
// Restyled to match the rest of the app (PerformancePeriods.js,
// GroupsView.js, ...): summary cards on top, then one bordered card with
// the title, filter bar, table and footer count. The charts are gone.
//
// Real backend (officeExpenseAPI) is unchanged. The date filters
// (This Month / Select Month / Custom) are what hit the backend, and they
// drive every card on top.
//
// Only the Total Expenses card has data today. Thika Office, Narok Office
// and Salaries are placeholders (shown blank) until the backend can tell
// us which office / salary expenses are which. To wire them up later: give
// each entry in SUMMARY_CARDS `wired: true`, add a value for it next to
// `cardValues` below, and (if you want them to act as tabs) a matcher
// applied in `filteredExpenses`.
// ---------------------------------------------------------------------

const SUMMARY_CARDS = [
  { key: 'all', label: 'Total Expenses', icon: Receipt, wired: true },
  { key: 'thika', label: 'Thika Office', icon: Building2, wired: false },
  { key: 'narok', label: 'Narok Office', icon: Building2, wired: false },
  { key: 'salaries', label: 'Salaries', icon: Banknote, wired: false },
];

const CATEGORIES = [
  { name: 'Food', icon: <Utensils className="w-3.5 h-3.5" />, color: '#4caf50' },
  { name: 'Transport', icon: <Car className="w-3.5 h-3.5" />, color: '#2196f3' },
  { name: 'Fuel', icon: <Fuel className="w-3.5 h-3.5" />, color: '#ff9800' },
  { name: 'Utilities', icon: <Home className="w-3.5 h-3.5" />, color: '#9c27b0' },
  { name: 'Shopping', icon: <ShoppingCart className="w-3.5 h-3.5" />, color: '#e91e63' },
  { name: 'Phone', icon: <Phone className="w-3.5 h-3.5" />, color: '#00bcd4' },
  { name: 'Paperwork', icon: <FileText className="w-3.5 h-3.5" />, color: '#795548' },
  { name: 'Meeting Expenses', icon: <Briefcase className="w-3.5 h-3.5" />, color: '#f44336' },
  { name: 'Salaries', icon: <Banknote className="w-3.5 h-3.5" />, color: '#16a34a' },
  { name: 'Other', icon: <MoreHorizontal className="w-3.5 h-3.5" />, color: '#607d8b' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Local-time YYYY-MM-DD. toISOString() converts to UTC first, which shifts
// "1st of the month at local midnight" back into the previous day for
// anyone ahead of UTC (e.g. Kenya, UTC+3).
const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Last day of a salary month (month is 1-12). Day 0 of the *next* month is
// the last day of this one, so leap years / 30 vs 31 days are handled.
const getSalaryExpenseDate = (month, year) => toLocalISO(new Date(year, month, 0));

const getThisMonthRange = () => {
  const today = new Date();
  return {
    startDate: toLocalISO(new Date(today.getFullYear(), today.getMonth(), 1)),
    endDate: toLocalISO(today),
  };
};

const ExpenseTracker = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // State management
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [salariesDialogOpen, setSalariesDialogOpen] = useState(false);

  // Date filter: 'month' (this month, default) | 'selectMonth' | 'custom'
  const [dateRange, setDateRange] = useState(getThisMonthRange());
  const [activeDatePreset, setActiveDatePreset] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuOpen, setMonthMenuOpen] = useState(false);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  const [formData, setFormData] = useState({
    date: toLocalISO(new Date()),
    category: 'Food',
    item: '',
    amount: '',
    description: '',
  });

  const [summary, setSummary] = useState({
    totalExpenses: 0,
    meetingExpenses: 0,
    officeExpenses: 0,
    transactionCount: 0,
    avgDaily: 0,
    categoriesUsed: 0
  });

  const getCategoryIcon = (categoryName) => {
    const category = CATEGORIES.find(cat => cat.name === categoryName);
    return category ? category.icon : <MoreHorizontal className="w-3.5 h-3.5" />;
  };

  const getCategoryColor = (categoryName) => {
    const category = CATEGORIES.find(cat => cat.name === categoryName);
    return category ? category.color : '#607d8b';
  };

  const fetchExpenses = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await officeExpenseAPI.getUnifiedExpenses({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        category: filterCategory !== 'all' ? filterCategory : undefined,
        search: searchTerm || undefined
      });

      if (response.success) {
        setExpenses(response.data.expenses);
        setSummary(response.data.summary);
      } else {
        throw new Error('Failed to fetch expenses');
      }
    } catch (err) {
      const errorMsg = handleAPIError(err);
      setError(errorMsg);
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [dateRange, filterCategory]);

  // ---- Date filter handlers ----
  const handleThisMonth = () => {
    setDateRange(getThisMonthRange());
    setSelectedMonth('');
    setActiveDatePreset('month');
  };

  const handleSelectMonth = (month) => {
    const start = new Date(month.year, month.monthIndex, 1);
    const end = new Date(month.year, month.monthIndex + 1, 0);
    setDateRange({ startDate: toLocalISO(start), endDate: toLocalISO(end) });
    setSelectedMonth(month.value);
    setActiveDatePreset('selectMonth');
    setMonthMenuOpen(false);
  };

  const handleCustomDateApply = () => {
    if (!tempDateRange.startDate || !tempDateRange.endDate) return;
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    return new Date(`${dateString}T00:00:00`).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const getDateRangeLabel = () => {
    if (activeDatePreset === 'month') return 'This Month';
    if (activeDatePreset === 'selectMonth' && selectedMonth) {
      const [year, monthIndex] = selectedMonth.split('-');
      return `${MONTH_NAMES[parseInt(monthIndex)]} ${year}`;
    }
    return `${formatShortDate(dateRange.startDate)} – ${formatShortDate(dateRange.endDate)}`;
  };

  // ---- Add / edit / delete (backend logic unchanged) ----
  const handleOpenDialog = (expense = null) => {
    if (expense) {
      if (expense.expenseType === 'meeting') {
        setSnackbar({
          open: true,
          message: 'Meeting expenses cannot be edited from the tracker',
          severity: 'warning'
        });
        return;
      }

      const expenseId = expense.id.replace('office-', '');
      setEditingExpense({ ...expense, id: expenseId });
      setFormData({
        date: expense.date,
        category: expense.category,
        item: expense.item,
        amount: expense.amount,
        description: expense.description,
      });
    } else {
      setEditingExpense(null);
      setFormData({
        date: toLocalISO(new Date()),
        category: 'Food',
        item: '',
        amount: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
  };

  // Record Salaries. Receives { month (1-12), year, totalSalaries } from
  // RecordSalariesDialog and saves it as an ordinary office expense, so no
  // extra backend fields are needed:
  //   category = 'Salaries', item = 'Salaries'
  //   date     = LAST DAY of the salary month (e.g. salary month Sept,
  //              recorded in October -> 30 Sept). The backend still stamps
  //              its own created-at as today.
  // Throws on failure so the dialog stays open and shows the error.
  const handleSaveSalaries = async ({ month, year, totalSalaries }) => {
    const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long' });
    try {
      await officeExpenseAPI.createOfficeExpense({
        date: getSalaryExpenseDate(month, year),
        category: 'Salaries',
        item: 'Salaries',
        amount: totalSalaries,
        description: `Salaries for ${monthName} ${year}`,
      });
      setSnackbar({
        open: true,
        message: `Salaries for ${monthName} ${year} recorded`,
        severity: 'success',
      });
      fetchExpenses();
    } catch (err) {
      throw new Error(handleAPIError(err));
    }
  };

  const handleSaveExpense = async () => {
    if (!formData.item || !formData.amount) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const expenseData = {
        date: formData.date,
        category: formData.category,
        item: formData.item,
        amount: parseFloat(formData.amount),
        description: formData.description,
      };

      if (editingExpense) {
        await officeExpenseAPI.updateOfficeExpense(editingExpense.id, expenseData);
        setSnackbar({ open: true, message: 'Expense updated successfully', severity: 'success' });
      } else {
        await officeExpenseAPI.createOfficeExpense(expenseData);
        setSnackbar({ open: true, message: 'Expense created successfully', severity: 'success' });
      }

      handleCloseDialog();
      fetchExpenses();
    } catch (err) {
      const errorMsg = handleAPIError(err);
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (expense.expenseType === 'meeting') {
      setSnackbar({
        open: true,
        message: 'Meeting expenses cannot be deleted from the tracker',
        severity: 'warning'
      });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    setLoading(true);
    try {
      const expenseId = expense.id.replace('office-', '');
      await officeExpenseAPI.deleteOfficeExpense(expenseId);
      setSnackbar({ open: true, message: 'Expense deleted successfully', severity: 'success' });
      fetchExpenses();
    } catch (err) {
      const errorMsg = handleAPIError(err);
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.item.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  // Value shown on each summary card. Only Total has data today — the
  // others stay blank until the backend can split expenses by office /
  // salaries (see the note at the top of this file).
  const cardValues = {
    all: `KSh ${(summary.totalExpenses || 0).toLocaleString()}`,
    thika: null,
    narok: null,
    salaries: null,
  };

  const thClass = (align = 'left') =>
    `px-6 py-3 text-${align} text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  const filterButtonClass = (active) =>
    `px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
      active
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
        : isDark
          ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
          : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
    } focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="pb-32">
      {/* Summary cards — values follow the date filters below */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {SUMMARY_CARDS.map((card) => {
          const Icon = card.icon;
          const value = cardValues[card.key];
          const isActive = card.key === 'all';
          return (
            <div
              key={card.key}
              className={`text-left rounded-lg border p-4 ${
                isActive
                  ? isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'
                  : isDark ? 'border-gray-700 bg-[#2b323c]' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold min-h-[2rem] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.wired && value !== null ? value : ''}
              </div>
              {card.key === 'all' && (
                <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {summary.transactionCount} transaction{summary.transactionCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <div className={`rounded-lg px-4 py-3 mb-4 flex items-center justify-between ${isDark ? 'bg-red-900/20 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'}`}>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-sm font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Expense Tracker</h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Period: {getDateRangeLabel()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchExpenses}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setSalariesDialogOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              <Banknote className="w-4 h-4" />
              Record Salaries
            </button>
            <button
              onClick={() => handleOpenDialog()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Expense
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchExpenses()}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.name} value={cat.name}>{cat.name}</option>
            ))}
          </select>

          {/* This Month - default range */}
          <button onClick={handleThisMonth} className={filterButtonClass(activeDatePreset === 'month')}>
            <Calendar className="w-4 h-4" />
            This Month
          </button>

          {/* Select Month */}
          <button
            onClick={() => setMonthMenuOpen(true)}
            className={`${filterButtonClass(activeDatePreset === 'selectMonth')} justify-between`}
          >
            <span>
              {selectedMonth !== ''
                ? (() => {
                    const [year, monthIndex] = selectedMonth.split('-');
                    return `${MONTH_NAMES[parseInt(monthIndex)]} ${year}`;
                  })()
                : 'Select Month'}
            </span>
            <CalendarDays className="w-4 h-4 ml-2" />
          </button>

          {/* Custom */}
          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={filterButtonClass(activeDatePreset === 'custom')}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading expenses...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <tr>
                  <th className={thClass('left')}>Date</th>
                  <th className={thClass('left')}>Type</th>
                  <th className={thClass('left')}>Category</th>
                  <th className={thClass('left')}>Item</th>
                  <th className={thClass('left')}>Description</th>
                  <th className={thClass('right')}>Amount</th>
                  <th className={thClass('center')}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredExpenses.map((expense) => (
                  <tr key={expense.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {new Date(expense.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white"
                        style={{ backgroundColor: expense.expenseType === 'meeting' ? '#f44336' : '#2196f3' }}
                      >
                        {expense.expenseType === 'meeting'
                          ? <Briefcase className="w-3.5 h-3.5" />
                          : <Calendar className="w-3.5 h-3.5" />}
                        {expense.expenseType === 'meeting' ? 'Meeting' : 'Office'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white"
                        style={{ backgroundColor: getCategoryColor(expense.category) }}
                      >
                        {getCategoryIcon(expense.category)}
                        {expense.category}
                      </span>
                    </td>
                    <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {expense.item}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {expense.description}
                    </td>
                    <td className={`px-6 py-4 text-right font-semibold whitespace-nowrap ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                      KSh {expense.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      {expense.editable ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleOpenDialog(expense)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm font-medium rounded-lg transition-colors ${
                              isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                            }`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm font-medium rounded-lg transition-colors ${
                              isDark ? 'border-red-800 text-red-400 hover:border-red-500' : 'border-red-300 text-red-600 hover:border-red-500'
                            }`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      ) : (
                        <div className={`text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>View Only</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredExpenses.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No expenses found for this period</p>
            <p className="text-sm mt-1">Try adjusting your filters, or add your first expense.</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredExpenses.length} of {expenses.length} expenses
        </div>
      </div>

      {/* Month Selection Menu */}
      {monthMenuOpen && (
        <div className="fixed inset-0 z-[1400] flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${MONTH_NAMES[monthIndex]} ${year}`,
                  monthIndex,
                  year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => handleSelectMonth(month)}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-[#1e252d] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-[#1e252d] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!tempDateRange.startDate || !tempDateRange.endDate}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog — left as MUI, unchanged (same scope decision as
         the other real pages' form dialogs). */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingExpense ? 'Edit Office Expense' : 'Add New Office Expense'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {CATEGORIES.filter(cat => cat.name !== 'Meeting Expenses').map(cat => (
                    <MenuItem key={cat.name} value={cat.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {cat.icon}
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name"
                value={formData.item}
                onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KSh</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveExpense}
            disabled={!formData.item || !formData.amount || loading}
          >
            {editingExpense ? 'Update' : 'Add'} Expense
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Salaries dialog (design stage - see handleSaveSalaries) */}
      <RecordSalariesDialog
        open={salariesDialogOpen}
        onClose={() => setSalariesDialogOpen(false)}
        onSave={handleSaveSalaries}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ExpenseTracker;